import { TypedDocumentNode } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  MemberDeleteOptions,
  Mutation,
  MutationAddVisitorGroupArgs,
  MutationDeleteVisitorGroupByIdArgs,
  MutationSendInvitationForMobileDeviceByIdArgs,
  MutationSetVisitorByIdArgs,
  MutationUnregisterMobileDeviceByIdArgs,
  MutationUpdateVisitorGroupArgs,
  MutationUpdateVisitorGroupDetailsArgs,
  Query,
  VisitorGroup,
  VisitorGroupMembershipInput
} from 'graphql/generated/graphqlTypes';
import {
  ADD_VISITOR_GROUP_V2,
  DELETE_VISITOR_GROUP_BY_ID,
  SEND_INVITATION_FOR_MOBILE_DEVICE_BY_ID,
  SET_VISITOR_BY_ID_WITH_ROLE,
  UNREGISTER_MOBILE_DEVICE_BY_ID,
  UPDATE_VISITOR_GROUP_DETAILS_V2,
  UPDATE_VISITOR_GROUP_V2
} from 'graphql/v2/mutations';
import {
  GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS,
  GET_VISITOR_DETAILS,
  GET_VISITOR_GROUPS_WITHOUT_NESTED_RESOLVERS
} from 'graphql/v2/queries';
import { useEffect, useMemo } from 'react';
import { ignoreHandledMutationError } from 'utils/Mutation/ignoreHandledMutationError';
import { getAllAccessGrantsOfVisitorGroup, VisitorGroupType } from 'utils/VisitorGroup/VisitorGroup';
import { TranslatedHookProps } from '../hooks.types';

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
const GET_VISITOR_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getVisitorGroupsForTenant'>> =
  GET_VISITOR_GROUPS_WITHOUT_NESTED_RESOLVERS;
const ADD_VISITOR_GROUP_V2_TYPED: TypedDocumentNode<
  Pick<Mutation, 'addVisitorGroup'>,
  MutationAddVisitorGroupArgs
> = ADD_VISITOR_GROUP_V2;
const UPDATE_VISITOR_GROUP_V2_TYPED: TypedDocumentNode<
  Pick<Mutation, 'updateVisitorGroup'>,
  MutationUpdateVisitorGroupArgs
> = UPDATE_VISITOR_GROUP_V2;
const DELETE_VISITOR_GROUP_BY_ID_TYPED: TypedDocumentNode<
  Pick<Mutation, 'deleteVisitorGroupById'>,
  MutationDeleteVisitorGroupByIdArgs
> = DELETE_VISITOR_GROUP_BY_ID;
const SET_VISITOR_BY_ID_WITH_ROLE_TYPED: TypedDocumentNode<
  Pick<Mutation, 'setVisitorById'>,
  MutationSetVisitorByIdArgs
> = SET_VISITOR_BY_ID_WITH_ROLE;
const SEND_INVITATION_FOR_MOBILE_DEVICE_BY_ID_TYPED: TypedDocumentNode<
  Pick<Mutation, 'sendInvitationForMobileDeviceById'>,
  MutationSendInvitationForMobileDeviceByIdArgs
> = SEND_INVITATION_FOR_MOBILE_DEVICE_BY_ID;
const UNREGISTER_MOBILE_DEVICE_BY_ID_TYPED: TypedDocumentNode<
  Pick<Mutation, 'unregisterMobileDeviceById'>,
  MutationUnregisterMobileDeviceByIdArgs
> = UNREGISTER_MOBILE_DEVICE_BY_ID;
const UPDATE_VISITOR_GROUP_DETAILS_V2_TYPED: TypedDocumentNode<
  Pick<Mutation, 'updateVisitorGroupDetails'>,
  MutationUpdateVisitorGroupDetailsArgs
> = UPDATE_VISITOR_GROUP_DETAILS_V2;

const useExternalUsers = ({ translation, handleFetchError }: TranslatedHookProps) => {
  const { data: externalUsersData, error: externalUsersError } = useQuery(
    GET_VISITOR_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED
  );

  useEffect(() => {
    if (externalUsersError) handleFetchError(translation.user_groups_error_fetching);
  }, [externalUsersError]);

  // The query returns every group, so keep only the co-contractor ones that the server filter used to return.
  const externalUsersGroups = useMemo(
    () =>
      (externalUsersData?.getVisitorGroupsForTenant ?? []).filter(
        (group) => group.type === VisitorGroupType.CoContractors
      ),
    [externalUsersData]
  );

  const [addVisitorGroupWithMaxNumberOfMembersMutation] = useMutation(ADD_VISITOR_GROUP_V2_TYPED, {
    // Object style, so the group is in the list before the wizard navigates to its details page.
    refetchQueries: [{ query: GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS }],
    awaitRefetchQueries: true,
    onError: () => handleFetchError(translation.user_groups_error_adding)
  });

  const [
    updateVisitorGroupWithMaxNumberOfMembersMutation,
    { loading: updateVisitorGroupWithMaxNumberOfMembersMutationLoading }
  ] = useMutation(UPDATE_VISITOR_GROUP_V2_TYPED, {
    onError: () => handleFetchError(translation.user_groups_error_updating)
  });

  const [deleteVisitorGroupByIdMutation, { loading: deleteVisitorGroupByIdMutationLoading }] = useMutation(
    DELETE_VISITOR_GROUP_BY_ID_TYPED,
    {
      refetchQueries: [{ query: GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS }],
      awaitRefetchQueries: true,
      onError: (error) => handleFetchError(error.message)
    }
  );

  const [sendInvitationForMobileDeviceByIdMutation, { loading: sendInvitationForMobileDeviceByIdMutationLoading }] =
    useMutation(SEND_INVITATION_FOR_MOBILE_DEVICE_BY_ID_TYPED, {
      onError: (error) => {
        if (
          error.message.includes('Too many requests') ||
          error.message === 'MobileDevice does not have registration token'
        )
          handleFetchError(translation.invite_rate_limit_error);
        else handleFetchError(translation.error_while_resending_invitation);
      }
    });

  const [unregisterMobileDeviceByIdMutation, { loading: unregisterMobileDeviceByIdMutationLoading }] = useMutation(
    UNREGISTER_MOBILE_DEVICE_BY_ID_TYPED,
    {
      refetchQueries: [GET_VISITOR_DETAILS],
      awaitRefetchQueries: true,
      onError: () => handleFetchError(translation.unregister_mobile_device_error)
    }
  );

  const addVisitorGroupWithMaxNumberOfMembers = async (name: string, maxNumberOfMembers: number | null) => {
    const { data } = await addVisitorGroupWithMaxNumberOfMembersMutation({
      variables: { name, maxNumberOfMembers, type: VisitorGroupType.CoContractors }
    });
    return data;
  };

  const updateVisitorGroupWithMaxNumberOfMembers = async (
    name: string,
    visitorGroupId: string,
    maxNumberOfMembers: number | null
  ) => {
    const { data } = await updateVisitorGroupWithMaxNumberOfMembersMutation({
      variables: { name, visitorGroupId, maxNumberOfMembers }
    });
    return data;
  };

  const deleteVisitorGroupById = async (visitorGroupId: string, options: MemberDeleteOptions) => {
    const { data } = await deleteVisitorGroupByIdMutation({
      variables: { visitorGroupId, options }
    });
    return data;
  };

  const sendInvitationForMobileDeviceById = async (visitorId: string, mobileDeviceId: string) => {
    const { data } = await ignoreHandledMutationError(
      sendInvitationForMobileDeviceByIdMutation({ variables: { visitorId, mobileDeviceId } })
    );
    return data;
  };

  const unregisterMobileDeviceById = async (visitorId: string, mobileDeviceId: string) => {
    const { data } = await ignoreHandledMutationError(
      unregisterMobileDeviceByIdMutation({ variables: { visitorId, mobileDeviceId } })
    );
    return data;
  };

  const [updateVisitorGroupDetailsMutation] = useMutation(UPDATE_VISITOR_GROUP_DETAILS_V2_TYPED, {
    refetchQueries: (result) => [
      {
        query: GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS,
        variables: { filter: { id: result.data?.updateVisitorGroupDetails?.id } }
      }
    ],
    awaitRefetchQueries: true
  });

  type UpdateVisitorGroupDetailsArg = {
    visitorGroup: VisitorGroup;
    name?: string;
    // undefined = leave unchanged, null = clear the limit (must be sent explicitly, omitting the field does not clear it)
    maxNumberOfMembers?: number | null;
    isOfficeModeEnabled: boolean;
    isQrCodeVisitInviter: boolean;
  };

  const updateVisitorGroupDetails = async ({
    visitorGroup,
    name,
    maxNumberOfMembers,
    isOfficeModeEnabled,
    isQrCodeVisitInviter
  }: UpdateVisitorGroupDetailsArg): Promise<{ success: boolean }> => {
    const accessGrantId = getAllAccessGrantsOfVisitorGroup(visitorGroup)[0]?.id;
    if (!accessGrantId) {
      handleFetchError(translation.user_groups_error_updating);
      return { success: false };
    }

    try {
      const result = await updateVisitorGroupDetailsMutation({
        variables: {
          input: {
            visitorGroupId: visitorGroup.id,
            name,
            maxNumberOfMembers,
            isQrCodeVisitInviter,
            officeMode: {
              accessGrantId,
              isOfficeModeEnabled
            }
          }
        }
      });
      if (!result.data?.updateVisitorGroupDetails?.id) {
        handleFetchError(translation.user_groups_error_updating);
        return { success: false };
      }
      return { success: true };
    } catch (err) {
      handleFetchError(translation.user_groups_error_updating);
      console.error(err);
      return { success: false };
    }
  };

  const [setVisitorInfoByIdWithRoleMutation] = useMutation(SET_VISITOR_BY_ID_WITH_ROLE_TYPED, {
    refetchQueries: [{ query: GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS }],
    awaitRefetchQueries: true
  });

  type SetVisitorByIdWithRoleArg = {
    visitorId: string;
    name: string;
    visitorGroupMembership: VisitorGroupMembershipInput[];
  };

  const setVisitorByIdWithRole = async ({ visitorId, name, visitorGroupMembership }: SetVisitorByIdWithRoleArg) => {
    try {
      await setVisitorInfoByIdWithRoleMutation({
        variables: { visitorId, name, visitorGroupMembership }
      });
    } catch (err) {
      console.error(err);
      if (err instanceof Error && err.message.includes('Installers cannot have expiration date')) {
        handleFetchError(translation.expiration_date_error_installer);
      } else {
        handleFetchError(translation.external_groups_user_error_updating);
      }
      throw err;
    }
  };

  return {
    externalUsersGroups,
    addVisitorGroupWithMaxNumberOfMembers,
    updateVisitorGroupWithMaxNumberOfMembers,
    updateVisitorGroupWithMaxNumberOfMembersMutationLoading,
    deleteVisitorGroupById,
    deleteVisitorGroupByIdMutationLoading,
    sendInvitationForMobileDeviceById,
    sendInvitationForMobileDeviceByIdMutationLoading,
    unregisterMobileDeviceById,
    unregisterMobileDeviceByIdMutationLoading,
    setVisitorByIdWithRole,
    updateVisitorGroupDetails
  };
};

export default useExternalUsers;
